import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Compass, Zap, Award } from "lucide-react";
import AuraGlow from "@/components/AuraGlow";
import FadeIn from "@/components/FadeIn";

const websiteExamples = [
  "/images/website-examples/example-1.webp",
  "/images/website-examples/example-2.webp",
  "/images/website-examples/example-3.webp",
  "/images/website-examples/example-4.webp",
  "/images/website-examples/example-5.webp",
];

function WebsiteCarousel() {
  const [current, setCurrent] = useState(0);

  const nextImage = () => {
    setCurrent((current + 1) % websiteExamples.length);
  };

  const previousImage = () => {
    setCurrent((current - 1 + websiteExamples.length) % websiteExamples.length);
  };

  return (
    <div className="relative h-[420px] w-full overflow-hidden rounded-2xl border border-[#1E293B] shadow-2xl bg-[#0E1422]">
      <img
        src={websiteExamples[current]}
        alt="Premium website example"
        loading="lazy"
        className="h-full w-full object-cover transition-all duration-500"
      />

      <button
        onClick={previousImage}
        className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/60 px-4 py-2 text-2xl text-white hover:bg-black/80"
      >
        ‹
      </button>

      <button
        onClick={nextImage}
        className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/60 px-4 py-2 text-2xl text-white hover:bg-black/80"
      >
        ›
      </button>

      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
        {websiteExamples.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrent(index)}
            className={`h-2.5 w-2.5 rounded-full ${
              current === index ? "bg-[#4DA3FF]" : "bg-white/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

const values = [
  {
    icon: Heart,
    title: "Small businesses first",
    desc: "We genuinely enjoy helping small UK businesses look bigger, sharper and more credible online.",
  },
  {
    icon: Zap,
    title: "Fast, but never rushed",
    desc: "Most websites ship in 7–14 days — without cutting corners on design, performance or quality.",
  },
  {
    icon: Compass,
    title: "Outcome focused",
    desc: "We design for outcomes — more enquiries, more bookings, more sales — not vanity metrics.",
  },
  {
    icon: Award,
    title: "Premium feel, fair price",
    desc: "Boutique-studio design without the boutique-studio price tag. Built to be sustainable for both sides.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 md:pt-40 pb-20" data-testid="about-hero">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <AuraGlow className="left-1/2 top-[20%] -translate-x-1/2" size={620} blur={170} opacity={0.18} drift />
        <div className="container-7 relative">
          <FadeIn>
            <p className="overline">About us</p>
          </FadeIn>
          <FadeIn delay={120}>
            <h1 className="mt-4 max-w-4xl text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05]">
              We build websites that <span className="gradient-text">help small businesses win.</span>
            </h1>
          </FadeIn>
          <FadeIn delay={220}>
            <p className="mt-6 max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
              WebAuraSites is a UK-based web design studio with one focus —
              giving small businesses an online presence they're genuinely proud of.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* STORY */}
      <section className="py-16 md:py-24" data-testid="about-story">
        <div className="container-7 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <AuraGlow className="-right-10 top-10" size={320} blur={130} opacity={0.18} />
              <div className="surface-card overflow-hidden">
                <WebsiteCarousel />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="overline">Our story</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
              Built out of frustration with bad small-business websites.
            </h2>
            <div className="mt-6 space-y-5 text-[#94A3B8] leading-relaxed">
              <p>
                WebAuraSites was created after seeing too many great UK businesses held
                back by tired, slow, "DIY-feel" websites that didn't reflect how good
                they really were in person.
              </p>
              <p>
                We built this studio to fix that. To bring boutique-agency quality
                design and engineering — fast delivery, modern aesthetics, and conversion
                thinking baked in — to small businesses that wouldn't normally have
                access to it.
              </p>
              <p>
                Today, we work with mechanics, salons, gyms, cosmetics brands, tradespeople
                and growing service businesses across the UK — and we're scaling to support
                ambitious teams of every kind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-28 bg-[#0E1422] border-y border-[#1E293B]" data-testid="about-values">
        <div className="container-7">
          <div className="max-w-2xl">
            <p className="overline">What we believe</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium">
              Four ideas that shape every project.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((v, i) => (
              <FadeIn key={v.title} delay={i * 100}>
                <div className="surface-card p-7 hover-card" data-testid={`value-card-${i}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1A2235] border border-[#1E293B] text-[#4DA3FF]">
                    <v.icon size={20} strokeWidth={1.6} />
                  </div>
                  <h4 className="mt-5 text-lg font-medium">{v.title}</h4>
                  <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">{v.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="about-cta">
        <div className="container-7">
          <div className="relative overflow-hidden surface-card p-10 md:p-14">
            <AuraGlow className="left-[15%] top-1/2 -translate-y-1/2" size={500} blur={150} opacity={0.18} />
            <div className="relative max-w-2xl">
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">
                Want to work with us?
              </h2>
              <p className="mt-4 text-[#94A3B8]">
                We take on a small number of projects each month so every client gets our
                full attention. Tell us about your business — we'd love to hear from you.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
                  Get in touch
                  <ArrowRight size={16} />
                </Link>
                <Link to="/services" className="btn-secondary" data-testid="about-cta-services">
                  Explore services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
